<template>
  <form novalidate @submit.prevent="submit">
    <c-heading as="h3" size="sm" :my="{ base: 'md', md: '3xl' }">
      {{ $t("productReturn.salesReturn.subtitle") }}
    </c-heading>

    <c-box
      :box-shadow="{ md: 'base' }"
      :border-radius="{ md: 'base' }"
      bg="white"
      :px="{ base: 'md', md: '4xl' }"
      :pt="{ base: 'xl', md: '2.5rem' }"
      :pb="{ base: '2xl', md: '3rem' }"
      :mx="{ base: '-15px', md: '0' }"
    >
      <c-form-control
        :is-invalid="inputIsInvalid('campaignReturnKeys')"
        :mb="{ base: 'lg', xl: '2.5rem' }"
      >
        <c-box
          display="flex"
          :flex-direction="{ base: 'column', sm: 'row' }"
          align-items="baseline"
          :mr="{ xl: 'md' }"
        >
          <c-form-label :min-width="{ xl: '18rem' }" for="campaignReturnKeys">
            {{ $t("productReturn.campaignReturn.keys.qty") }}
          </c-form-label>
          <c-box flex="1" width="100%">
            <c-flex gap="md" align-items="center">
              <c-input
                id="campaignReturnKeys"
                data-cy="campaignReturnKeys"
                v-model.trim="form.campaignReturnKeys"
                :placeholder="$t('common.quantity')"
                @keyup="onFieldChange('campaignReturnKeys')"
                @blur="onFieldBlur('campaignReturnKeys')"
                size="lg"
                :max-w="{ xl: '12rem' }"
              />
              <c-text font-size="md">{{ $t("common.pcs") }}</c-text>
            </c-flex>
            <c-box
              v-if="isErrorField('campaignReturnKeys')"
              color="brand.red"
              font-size="sm"
              my="xs"
            >
              {{
                $t("common.error.fieldInvalid", {
                  field: $t("productReturn.campaignReturn.keys.qty"),
                })
              }}
            </c-box>
          </c-box>
        </c-box>
      </c-form-control>

      <c-form-control
        :is-invalid="inputIsInvalid('campaignReturnLocksAndCylinders')"
        :mb="{ base: 'lg', xl: '2.5rem' }"
      >
        <c-box
          display="flex"
          :flex-direction="{ base: 'column', sm: 'row' }"
          align-items="baseline"
          :mr="{ xl: 'md' }"
        >
          <c-form-label :min-width="{ xl: '18rem' }" for="campaignReturnLocksAndCylinders">
            {{ $t("productReturn.campaignReturn.locksAndCylinders.qty") }}
          </c-form-label>
          <c-box flex="1" width="100%">
            <c-flex gap="md" align-items="center">
              <c-input
                id="campaignReturnLocksAndCylinders"
                data-cy="campaignReturnLocksAndCylinders"
                v-model.trim="form.campaignReturnLocksAndCylinders"
                :placeholder="$t('common.quantity')"
                @keyup="onFieldChange('campaignReturnLocksAndCylinders')"
                @blur="onFieldBlur('campaignReturnLocksAndCylinders')"
                size="lg"
                :max-w="{ xl: '12rem' }"
              />
              <c-text font-size="md">{{ $t("common.pcs") }}</c-text>
            </c-flex>
            <c-box
              v-if="isErrorField('campaignReturnLocksAndCylinders')"
              color="brand.red"
              font-size="sm"
              my="xs"
            >
              {{
                $t("common.error.fieldInvalid", {
                  field: $t("productReturn.campaignReturn.locksAndCylinders.qty"),
                })
              }}
            </c-box>
          </c-box>
        </c-box>
      </c-form-control>

      <c-form-control
        :is-invalid="inputIsInvalid('campaignReturnOnlineProducts')"
        :mb="{ base: 'lg', xl: '2.5rem' }"
      >
        <c-box
          display="flex"
          :flex-direction="{ base: 'column', sm: 'row' }"
          align-items="baseline"
          :mr="{ xl: 'md' }"
        >
          <c-form-label :min-width="{ xl: '18rem' }" for="campaignReturnOnlineProducts">
            {{ $t("productReturn.campaignReturn.onlineProducts.qty") }}
          </c-form-label>
          <c-box flex="1" width="100%">
            <c-flex gap="md" align-items="center">
              <c-input
                id="campaignReturnOnlineProducts"
                data-cy="campaignReturnOnlineProducts"
                v-model.trim="form.campaignReturnOnlineProducts"
                :placeholder="$t('common.quantity')"
                @keyup="onFieldChange('campaignReturnOnlineProducts')"
                @blur="onFieldBlur('campaignReturnOnlineProducts')"
                size="lg"
                :max-w="{ xl: '12rem' }"
              />
              <c-text font-size="md">{{ $t("common.pcs") }}</c-text>
            </c-flex>
            <c-box
              v-if="isErrorField('campaignReturnOnlineProducts')"
              color="brand.red"
              font-size="sm"
              my="xs"
            >
              {{
                $t("common.error.fieldInvalid", {
                  field: $t("productReturn.campaignReturn.onlineProducts.qty"),
                })
              }}
            </c-box>
          </c-box>
        </c-box>
      </c-form-control>

      <c-form-control
        is-required
        :is-invalid="inputIsInvalid('campaignReturnOldLicenceId')"
        :mb="{ base: 'lg', xl: '2.5rem' }"
      >
        <c-box
          display="flex"
          :flex-direction="{ base: 'column', sm: 'row' }"
          :align-items="{ sm: 'center' }"
        >
          <c-form-label :width="{ sm: '18rem' }" for="campaignReturnOldLicenceId">
            {{ $t("productReturn.campaignReturn.campaignReturnOldLicenceId") }}
          </c-form-label>
          <c-box flex="1" width="100%">
            <c-input
              id="campaignReturnOldLicenceId"
              data-cy="campaignReturnOldLicenceId"
              v-model.trim="form.campaignReturnOldLicenceId"
              :placeholder="$t('productReturn.campaignReturn.campaignReturnOldLicenceId')"
              @keyup="onFieldChange('campaignReturnOldLicenceId')"
              @blur="onFieldBlur('campaignReturnOldLicenceId')"
              size="lg"
            />
            <c-box
              v-if="isErrorField('campaignReturnOldLicenceId')"
              color="brand.red"
              font-size="sm"
              my="xs"
            >
              {{
                $t("common.error.fieldInvalid", {
                  field: $t("productReturn.campaignReturn.campaignReturnOldLicenceId"),
                })
              }}
            </c-box>
          </c-box>
        </c-box>
      </c-form-control>

      <c-form-control
        is-required
        :is-invalid="inputIsInvalid('campaignReturnReplacementOrderNumber')"
        :mb="{ base: 'lg', xl: '2.5rem' }"
      >
        <c-box
          display="flex"
          :flex-direction="{ base: 'column', sm: 'row' }"
          :align-items="{ sm: 'center' }"
        >
          <c-form-label :width="{ sm: '18rem' }" for="campaignReturnReplacementOrderNumber">
            {{ $t("productReturn.campaignReturn.campaignReturnReplacementOrderNumber") }}
          </c-form-label>
          <c-box flex="1" width="100%">
            <c-input
              id="campaignReturnReplacementOrderNumber"
              data-cy="campaignReturnReplacementOrderNumber"
              v-model.trim="form.campaignReturnReplacementOrderNumber"
              :placeholder="$t('productReturn.campaignReturn.campaignReturnReplacementOrderNumber')"
              @keyup="onFieldChange('campaignReturnReplacementOrderNumber')"
              @blur="onFieldBlur('campaignReturnReplacementOrderNumber')"
              size="lg"
            />
            <c-box
              v-if="isErrorField('campaignReturnReplacementOrderNumber')"
              color="brand.red"
              font-size="sm"
              my="xs"
            >
              {{
                $t("common.error.fieldInvalid", {
                  field: $t("productReturn.campaignReturn.campaignReturnReplacementOrderNumber"),
                })
              }}
            </c-box>
          </c-box>
        </c-box>
      </c-form-control>

      <c-form-control
        :is-invalid="inputIsInvalid('campaignReturnProjectId')"
        :mb="{ base: 'lg', xl: '2.5rem' }"
      >
        <c-box
          display="flex"
          :flex-direction="{ base: 'column', sm: 'row' }"
          align-items="baseline"
        >
          <c-form-label :width="{ xl: '18rem' }" for="campaignReturnProjectId">
            {{ $t("productReturn.campaignReturn.projectId") }}
            {{ $t("productReturn.campaignReturn.projectIdFormat") }}
          </c-form-label>
          <c-box flex="1" width="100%">
            <c-input
              id="campaignReturnProjectId"
              data-cy="campaignReturnProjectId"
              v-model.trim="form.campaignReturnProjectId"
              :placeholder="$t('productReturn.campaignReturn.projectId')"
              @keyup="onFieldChange('campaignReturnProjectId')"
              @blur="onFieldBlur('campaignReturnProjectId')"
              size="lg"
            />
            <c-box
              v-if="isErrorField('campaignReturnProjectId')"
              color="brand.red"
              font-size="sm"
              my="xs"
            >
              {{
                $t("common.error.fieldInvalid", {
                  field: $t("productReturn.campaignReturn.projectId"),
                })
              }}
            </c-box>
          </c-box>
        </c-box>
      </c-form-control>
    </c-box>
  </form>
</template>

<script lang="ts">
import { FormValidationMixin } from "@/components/FormValidationMixin.js";
import Heading from "@/design-system/elements/Heading.vue";
import { CBox, CFlex, CFormControl, CFormLabel, CHeading, CInput, CText } from "@chakra-ui/vue";
import { rules } from "@iloq/shared";
import { defineComponent } from "vue";
import { alphaNum, maxLength, minLength, numeric, required } from "vuelidate/lib/validators";
import { inputMixin } from "./RMAMixins";

const requiredIfKeysExist = (value: string, siblings: any) => {
  if ((siblings.campaignReturnKeys || "").trim().length > 0) {
    return value?.trim().length > 0;
  }
  return true;
};

const requiredIfLocksAndCylindersExist = (value: string, siblings: any) => {
  if ((siblings.campaignReturnLocksAndCylinders || "").trim().length > 0) {
    return value?.trim().length > 0;
  }
  return true;
};

export default defineComponent({
  name: "RMA02CampaignReturnProducts",
  components: {
    Heading,
    CBox,
    CHeading,
    CFormControl,
    CFormLabel,
    CInput,
    CFlex,
    CText,
  },
  mixins: [inputMixin, FormValidationMixin],
  validations: {
    form: {
      campaignReturnKeys: { requiredIfLocksAndCylindersExist, numeric },
      campaignReturnLocksAndCylinders: {
        requiredIfKeysExist,
        numeric,
      },
      campaignReturnOnlineProducts: { numeric },
      campaignReturnOldLicenceId: { required, alphaNum },
      campaignReturnReplacementOrderNumber: { required, alphaNum },
      campaignReturnProjectId: {
        numeric,
        minLength: minLength(rules.minLength.global.projectId),
        maxLength: maxLength(rules.maxLength.global.projectId),
      },
    },
  },
  data() {
    return {
      form: {
        campaignReturnKeys: 0,
        campaignReturnLocksAndCylinders: 0,
        campaignReturnOnlineProducts: 0,
        campaignReturnOldLicenceId: "",
        campaignReturnReplacementOrderNumber: "",
        campaignReturnProjectId: "",
      },
    };
  },
  methods: {
    onErrors(ev: boolean) {
      this.$emit("errors", ev);
    },
    submit(): void {
      // Do nothing
    },
  },
});
</script>
